// opsIntegrationService.js
// Client for the IoT-Ops integration API (opsapi.theassetpro.com). Used by the
// AssetPro <-> IoT-Ops Device Comparison report to read the installed-device
// registry. READ-ONLY: this service only performs GETs.
//
// AUTH: the integration key (an `iotint_...` token) is pasted at runtime and
// persisted in localStorage. It is sent as the `X-INTEGRATION-KEY` header.
//
// CORS / PROXY: a browser cannot call opsapi directly (cross-origin + the custom
// header triggers a blocked preflight). All calls go through the same-origin
// `/ops-api/*` path, which is proxied to opsapi server-side. In local dev this is
// handled by src/setupProxy.js. NOTE (production TODO): setupProxy.js runs only
// under the CRA dev server; the Vercel build needs an equivalent proxy
// (serverless function or rewrite forwarding X-INTEGRATION-KEY) or these calls
// will fail in production. See docs/device-comparison-report.md.

const OPS_KEY_STORAGE = 'ops_integration_key';

// Same-origin base path proxied to https://opsapi.theassetpro.com.
const OPS_PROXY_BASE = '/ops-api';

const DEFAULT_PAGE_SIZE = 200;
const MAX_PAGES = 500; // safety cap (100k devices at 200/page); surfaced if hit.

export const getOpsKey = () => localStorage.getItem(OPS_KEY_STORAGE) || '';

export const setOpsKey = (key) => {
  const trimmed = String(key || '').trim();
  if (trimmed) localStorage.setItem(OPS_KEY_STORAGE, trimmed);
  else localStorage.removeItem(OPS_KEY_STORAGE);
};

export const hasOpsKey = () => Boolean(getOpsKey());

// Pull one page of the device registry.
const fetchDevicesPage = async (key, page, pageSize) => {
  const url = `${OPS_PROXY_BASE}/integration/devices?page=${page}&pageSize=${pageSize}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'X-INTEGRATION-KEY': key
    }
  });

  if (response.status === 401 || response.status === 403) {
    throw new Error('IoT-Ops rejected the integration key (unauthorized). Check the key and try again.');
  }

  const text = await response.text();
  let body;
  try {
    body = text ? JSON.parse(text) : null;
  } catch (e) {
    // A non-JSON body usually means the proxy isn't wired (dev: setupProxy not
    // loaded; prod: no Vercel proxy) and index.html was returned instead.
    throw new Error(
      `IoT-Ops returned a non-JSON response (status ${response.status}). ` +
      'The /ops-api proxy may not be configured for this environment.'
    );
  }

  if (!response.ok) {
    const msg = (body && (body.message || body.error)) || `IoT-Ops request failed with status ${response.status}`;
    throw new Error(msg);
  }
  return body;
};

// Extract the device rows array from a page payload across the shapes the API
// might use. Exact keys only — no guessing of unrelated arrays.
const extractRows = (body) => {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.items)) return body.items;
  if (Array.isArray(body.results)) return body.results;
  if (Array.isArray(body.devices)) return body.devices;
  return [];
};

// Read the reported total count (for pagination) if present.
const extractTotal = (body) => {
  if (!body || typeof body !== 'object') return null;
  const candidates = [body.total, body.totalCount, body.count, body.totalItems];
  const meta = body.meta || body.pagination || {};
  candidates.push(meta.total, meta.totalCount, meta.count, meta.totalItems);
  const found = candidates.find((v) => typeof v === 'number' && v >= 0);
  return found === undefined ? null : found;
};

/**
 * Fetch the full IoT-Ops device registry, paginating until all pages are read.
 * Returns { success, rows, error, meta }. On failure returns success:false with
 * an error message and never partial-but-silent data — meta.truncated flags a
 * hard page-cap stop so the UI can warn instead of implying completeness.
 */
export async function fetchOpsDevices({ pageSize = DEFAULT_PAGE_SIZE } = {}) {
  const key = getOpsKey();
  if (!key) {
    return { success: false, rows: [], error: 'No IoT-Ops integration key set. Paste an iotint_... key first.' };
  }

  const rows = [];
  let total = null;
  let pagesRead = 0;
  let truncated = false;

  try {
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const body = await fetchDevicesPage(key, page, pageSize);
      const pageRows = extractRows(body);
      rows.push(...pageRows);
      pagesRead = page;

      if (total === null) total = extractTotal(body);

      // Stop conditions: reported total reached, or a short/empty page (last page).
      if (total !== null && rows.length >= total) break;
      if (pageRows.length < pageSize) break;

      if (page === MAX_PAGES) {
        truncated = true; // hit the safety cap before exhausting pages
      }
    }
  } catch (err) {
    return { success: false, rows: [], error: err.message || 'Failed to fetch IoT-Ops devices' };
  }

  return {
    success: true,
    rows,
    error: null,
    meta: { total, fetched: rows.length, pagesRead, pageSize, truncated }
  };
}
